import { type City, getData, type Package } from '@/services/app.service';
import { createContext, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';

type ContextTypes = {
  cities: City[] | undefined;
  isLoading: boolean;
  prices: Record<string, number> | undefined;
  packagesByCity: Record<string, (Package & { isPromo: boolean })[]> | undefined;
};

const DataContext = createContext<ContextTypes | undefined>(undefined);

export const DataProvider = ({ children }: React.PropsWithChildren) => {
  const { data: cities, isLoading } = useQuery({
    queryKey: [getData.name],
    queryFn: getData,
  });

  const prices = useMemo(() => {
    if (!cities) {
      return undefined;
    }
    return cities?.reduce(
      (acc, city) => {
        acc[city.name] = city.vehicles[0]?.price;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [cities]);

  const packagesByCity = useMemo(() => {
    if (!cities) {
      return undefined;
    }
    return cities.reduce(
      (acc, city) => {
        const promo = (city.packages?.promo || []).map((pkg) => ({ ...pkg, isPromo: true }));
        const standard = (city.packages?.standard || []).map((pkg) => ({ ...pkg, isPromo: false }));
        acc[city.name] = [...promo, ...standard];
        return acc;
      },
      {} as Record<string, (Package & { isPromo: boolean })[]>,
    );
  }, [cities]);

  const value = useMemo(
    () => ({
      cities,
      isLoading,
      prices,
      packagesByCity,
    }),
    [cities, isLoading, prices, packagesByCity],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used in a DataProvider');
  }
  return context;
};
